import { useLive, useTicker } from '../../context/LiveContext';

function agoLabel(ms: number): string {
  const s = Math.floor(ms / 1000);
  if (s < 5) return 'just now';
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  return `${Math.floor(s / 3600)}h ago`;
}

/**
 * The header used to render a static word — "Live" or "Offline" — which told
 * you the socket existed but never that anything was happening on it. This
 * ticks, and pulses each time a check lands, so the dashboard reads as alive
 * rather than as a screenshot.
 */
export function LiveIndicator() {
  const { connected, lastEventAt } = useLive();
  useTicker(1000); // re-render so "4s ago" actually counts up

  const age = lastEventAt === null ? null : Date.now() - lastEventAt;
  // No traffic for two minutes on a live socket is worth showing.
  const stale = age !== null && age > 120_000;

  const dot = !connected ? 'bg-gray-400' : stale ? 'bg-status-degraded' : 'bg-status-up';

  const label = !connected
    ? 'Reconnecting…'
    : lastEventAt === null
      ? 'Waiting for checks'
      : agoLabel(age!);

  return (
    <span
      className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 tabular-nums"
      role="status"
      aria-label={connected ? `Live. Last check ${label}.` : 'Reconnecting to the server.'}
    >
      <span className="relative flex w-2 h-2 shrink-0">
        {connected && !stale && (
          <span
            aria-hidden="true"
            className={`absolute inset-0 rounded-full ${dot} animate-ping motion-reduce:hidden`}
          />
        )}
        <span aria-hidden="true" className={`relative w-2 h-2 rounded-full ${dot}`} />
      </span>
      <span className="hidden sm:inline">{label}</span>
    </span>
  );
}
